import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Columns, Filter, ArrowDownRight, ArrowUpRight, ChevronDown, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
const chartData = [{
  month: 'May-25',
  low: 10,
  medium: 10,
  high: 10
}, {
  month: 'Jun-25',
  low: 12,
  medium: 12,
  high: 8
}, {
  month: 'Jul-25',
  low: 15,
  medium: 15,
  high: 10
}, {
  month: 'Aug-25',
  low: 18,
  medium: 18,
  high: 14
}, {
  month: 'Sep-25',
  low: 20,
  medium: 22,
  high: 20
}, {
  month: 'Oct-25',
  low: 22,
  medium: 25,
  high: 23
}, {
  month: 'Nov-25',
  low: 25,
  medium: 30,
  high: 27
}, {
  month: 'Dec-25',
  low: 28,
  medium: 35,
  high: 32
}, {
  month: 'Jan-26',
  low: 30,
  medium: 38,
  high: 32
}, {
  month: 'Feb-26',
  low: 30,
  medium: 40,
  high: 30
}, {
  month: 'Mar-26',
  low: 30,
  medium: 40,
  high: 30
}, {
  month: 'Apr-26',
  low: 30,
  medium: 40,
  high: 30
}];
const tableData = [{
  id: '2',
  company: 'DUNDER MIFFLIN PAPER CO.',
  score: 82,
  trend: 3,
  dbt: 0,
  balance: 0,
  limit: 'Pending Decision',
  assignedTo: 'Sarah Chen',
  group: 'L'
}, {
  id: '1',
  company: 'WONKA INDUSTRIES',
  score: 88,
  trend: 7,
  dbt: 5,
  balance: 35000,
  limit: 100000,
  assignedTo: 'Dave Davidson',
  group: 'L'
}, {
  id: '5',
  company: 'INITECH',
  score: 28,
  trend: -12,
  dbt: 45,
  balance: 68000,
  limit: 75000,
  assignedTo: 'Sarah Chen',
  group: 'H'
}, {
  id: '3',
  company: 'WAYNE ENTERPRISES',
  score: 56,
  trend: -1,
  dbt: 29,
  balance: 38000,
  limit: 60000,
  assignedTo: 'Dave Davidson',
  group: 'M'
}, {
  id: '6',
  company: 'BIG KAHUNA BURGER',
  score: 28,
  trend: -8,
  dbt: 52,
  balance: 35000,
  limit: 37000,
  assignedTo: 'Sarah Chen',
  group: 'H'
}, {
  id: '7',
  company: 'BUY N LARGE',
  score: 56,
  trend: 2,
  dbt: 18,
  balance: 42000,
  limit: 80000,
  assignedTo: 'Dave Davidson',
  group: 'M'
}];
function formatCurrency(value: number) {
  return `$${(value / 1000).toFixed(0)}K`;
}
function getScoreColor(score: number) {
  if (score <= 30) return 'text-red-500 border-red-200 bg-red-50';
  if (score <= 70) return 'text-amber-500 border-amber-200 bg-amber-50';
  return 'text-green-500 border-green-200 bg-green-50';
}
function getRiskLevel(score: number): 'high' | 'moderate' | 'low' {
  if (score <= 30) return 'high';
  if (score <= 70) return 'moderate';
  return 'low';
}
function getTrendColor(trend: number) {
  if (trend < 0) return 'text-red-500';
  return 'text-green-500';
}
const CustomLegend = (props: any) => {
  const {
    payload
  } = props;
  return <div className="flex items-center justify-end gap-4 text-xs font-medium text-gray-500" data-id="element-2648">
      {payload.map((entry: any, index: number) => <div key={`item-${index}`} className="flex items-center gap-1.5" data-id="element-2649">
          <div className="w-3 h-3 rounded-sm" style={{
        backgroundColor: entry.color
      }} data-id="element-2650" />
          <span className="capitalize" data-id="element-2651">{entry.value}</span>
        </div>)}
    </div>;
};
interface MonitorPageProps {
  onCompanyClick: (company: string) => void;
}
export function MonitorPage({
  onCompanyClick
}: MonitorPageProps) {
  const [selectedRisk, setSelectedRisk] = useState<'all' | 'high' | 'moderate' | 'low'>('all');
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const filteredData = selectedRisk === 'all' ? tableData : tableData.filter(row => getRiskLevel(row.score) === selectedRisk);
  return <div className="p-6 max-w-[1600px] mx-auto w-full space-y-6" data-id="element-2652">
      {/* Page Title */}
      <h1 className="text-3xl font-bold text-gray-900" data-id="element-2653">
        Acme's Credit Dashboard
      </h1>

      {/* Top Section */}
      <div className="flex flex-col lg:flex-row gap-6" data-id="element-2654">
        {/* Stat Cards Grid */}
        <div className="grid grid-cols-2 gap-4 lg:w-[400px] shrink-0" data-id="element-2655">
          <button onClick={() => setSelectedRisk('all')} className={`bg-white rounded-xl border p-5 flex flex-col justify-center shadow-sm transition-all ${selectedRisk === 'all' ? 'border-2 border-gray-500 bg-gray-50' : selectedRisk !== 'all' ? 'border-gray-200 opacity-40' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2656">
            <div className={`text-4xl font-bold mb-1 ${selectedRisk === 'all' ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-2657">
              88
            </div>
            <div className={`text-xs font-bold tracking-wider uppercase ${selectedRisk === 'all' ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-2658">
              Total
              <br data-id="element-2659" />
              Accounts
            </div>
          </button>
          <button onClick={() => setSelectedRisk('high')} className={`bg-white rounded-xl border-2 p-5 flex flex-col justify-center shadow-sm transition-all ${selectedRisk === 'high' ? 'border-red-500 bg-red-50' : selectedRisk !== 'all' ? 'border-red-300 opacity-40' : 'border-red-400 hover:border-red-500'}`} data-id="element-2660">
            <div className={`text-4xl font-bold mb-1 ${selectedRisk === 'high' ? 'text-red-600' : 'text-red-400'}`} data-id="element-2661">
              19
            </div>
            <div className={`text-xs font-bold tracking-wider uppercase ${selectedRisk === 'high' ? 'text-red-600' : 'text-red-400'}`} data-id="element-2662">
              High Risk
            </div>
          </button>
          <button onClick={() => setSelectedRisk('moderate')} className={`bg-white rounded-xl border-2 p-5 flex flex-col justify-center shadow-sm transition-all ${selectedRisk === 'moderate' ? 'border-amber-500 bg-amber-50' : selectedRisk !== 'all' ? 'border-amber-300 opacity-40' : 'border-amber-400 hover:border-amber-500'}`} data-id="element-2663">
            <div className={`text-4xl font-bold mb-1 ${selectedRisk === 'moderate' ? 'text-amber-600' : 'text-amber-400'}`} data-id="element-2664">
              47
            </div>
            <div className={`text-xs font-bold tracking-wider uppercase ${selectedRisk === 'moderate' ? 'text-amber-600' : 'text-amber-400'}`} data-id="element-2665">
              Moderate Risk
            </div>
          </button>
          <button onClick={() => setSelectedRisk('low')} className={`bg-white rounded-xl border-2 p-5 flex flex-col justify-center shadow-sm transition-all ${selectedRisk === 'low' ? 'border-green-500 bg-green-50' : selectedRisk !== 'all' ? 'border-green-300 opacity-40' : 'border-green-400 hover:border-green-500'}`} data-id="element-2666">
            <div className={`text-4xl font-bold mb-1 ${selectedRisk === 'low' ? 'text-green-600' : 'text-green-400'}`} data-id="element-2667">
              22
            </div>
            <div className={`text-xs font-bold tracking-wider uppercase ${selectedRisk === 'low' ? 'text-green-600' : 'text-green-400'}`} data-id="element-2668">
              Low Risk
            </div>
          </button>
        </div>

        {/* Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 flex-1 shadow-sm min-h-[240px] flex flex-col" data-id="element-2669">
          <div className="flex items-center justify-start gap-3 mb-6" data-id="element-2670">
            {/* Groups Dropdown */}
            <div className="relative" data-id="element-2671">
              <button onClick={() => setShowGroupDropdown(!showGroupDropdown)} className="flex items-center justify-between gap-2 px-4 py-2 border-2 border-indigo-500 rounded-lg text-sm font-medium text-indigo-600 hover:bg-indigo-50 transition-colors min-w-[160px]" data-id="element-2672">
                <span className="text-indigo-500 text-xs font-semibold" data-id="element-2673">
                  Groups
                </span>
                <ChevronDown className="w-4 h-4 text-indigo-500" data-id="element-2674" />
              </button>
              {showGroupDropdown && <div className="absolute top-full mt-2 left-0 bg-white border border-gray-200 rounded-lg shadow-lg py-2 w-56 z-10" data-id="element-2675">
                  {['Ungrouped', 'Agriculture', 'Biotech', 'Building Materials', 'Construction', 'Consumer Goods', 'Energy', 'Financial Services', 'Healthcare', 'Manufacturing', 'Retail', 'Technology', 'Transportation'].map(group => <label key={group} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer" data-id="element-2676">
                      <input type="checkbox" checked={selectedGroups.includes(group)} onChange={e => {
                  if (e.target.checked) {
                    setSelectedGroups([...selectedGroups, group]);
                  } else {
                    setSelectedGroups(selectedGroups.filter(g => g !== group));
                  }
                }} className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" data-id="element-2677" />
                      <span className="text-sm text-gray-700" data-id="element-2678">{group}</span>
                    </label>)}
                </div>}
            </div>

            {/* Date Range Selector */}
            <div className="relative" data-id="element-2679">
              <input type="text" placeholder="MM/DD/YYYY – MM/DD/YYYY" className="pl-4 pr-10 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-64" data-id="element-2680" />
              <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-2681" />
            </div>
          </div>

          <div className="flex-1 min-h-[180px]" data-id="element-2682">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2683">
              <BarChart data={chartData} margin={{
              top: 0,
              right: 0,
              left: -20,
              bottom: 0
            }} barSize={24} data-id="element-2684">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2685" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#6B7280'
              }} dy={10} data-id="element-2686" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#6B7280'
              }} domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} data-id="element-2687" />
                <Tooltip cursor={{
                fill: '#F3F4F6'
              }} contentStyle={{
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-2688" />
                <Legend content={<CustomLegend data-id="element-2690" />} verticalAlign="top" align="right" wrapperStyle={{
                top: -40
              }} data-id="element-2689" />
                <Bar dataKey="high" stackId="a" fill="#f87171" name="High" data-id="element-2691" />
                <Bar dataKey="medium" stackId="a" fill="#fbbf24" name="Medium" data-id="element-2692" />
                <Bar dataKey="low" stackId="a" fill="#4ade80" name="Low" data-id="element-2693" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col" data-id="element-2694">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white" data-id="element-2695">
          <h3 className="text-lg font-bold text-gray-900" data-id="element-2696">
            Monitored Accounts
          </h3>
          <div className="flex items-center gap-4" data-id="element-2697">
            <button className="flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700" data-id="element-2698">
              <Columns className="w-4 h-4" data-id="element-2699" />
              COLUMNS
            </button>
            <button className="flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700" data-id="element-2700">
              <Filter className="w-4 h-4" data-id="element-2701" />
              FILTERS
            </button>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-2702">
          <table className="w-full text-left text-sm" data-id="element-2703">
            <thead className="bg-white border-b border-gray-100" data-id="element-2704">
              <tr data-id="element-2705">
                <th className="px-6 py-4 font-semibold text-gray-500 text-xs tracking-wider uppercase whitespace-nowrap" data-id="element-2706">
                  Company
                </th>
                <th className="px-6 py-4 font-semibold text-gray-500 text-xs tracking-wider uppercase whitespace-nowrap" data-id="element-2707">
                  Pulse Score
                </th>
                <th className="px-6 py-4 font-semibold text-gray-500 text-xs tracking-wider uppercase whitespace-nowrap" data-id="element-2708">
                  1M Trend
                </th>
                <th className="px-6 py-4 font-semibold text-gray-500 text-xs tracking-wider uppercase whitespace-nowrap" data-id="element-2709">
                  DBT
                </th>
                <th className="px-6 py-4 font-semibold text-gray-500 text-xs tracking-wider uppercase whitespace-nowrap" data-id="element-2710">
                  Balance / Limit
                </th>
                <th className="px-6 py-4 font-semibold text-gray-500 text-xs tracking-wider uppercase whitespace-nowrap" data-id="element-2711">
                  Assigned To
                </th>
                <th className="px-6 py-4 font-semibold text-gray-500 text-xs tracking-wider uppercase whitespace-nowrap" data-id="element-2712">
                  Group
                </th>
                <th className="px-6 py-4 font-semibold text-gray-500 text-xs tracking-wider uppercase text-right whitespace-nowrap" data-id="element-2713">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-2714">
              {filteredData.map(row => {
              const utilPct = typeof row.limit === 'number' && row.limit > 0 ? Math.round(row.balance / row.limit * 100) : 0;
              const isDunderMifflin = row.id === '2';
              const isWonka = row.id === '1';
              const isInitech = row.id === '5';
              const isClickable = isDunderMifflin || isWonka || isInitech;
              return <tr key={row.id} onClick={isClickable ? () => onCompanyClick(isWonka ? 'wonka' : isInitech ? 'initech' : 'dunder') : undefined} className={`group transition-all ${isClickable ? 'cursor-pointer hover:bg-indigo-100' : 'hover:bg-indigo-50/30'}`} data-id="element-2715">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2716">
                      <span className="font-bold text-gray-900" data-id="element-2717">
                        {row.company}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2718">
                      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full border-2 font-bold ${getScoreColor(row.score)}`} data-id="element-2719">
                        {row.score}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2720">
                      <div className={`flex items-center gap-1 font-semibold ${getTrendColor(row.trend)}`} data-id="element-2721">
                        {row.trend < 0 ? <ArrowDownRight className="w-4 h-4" data-id="element-2722" /> : <ArrowUpRight className="w-4 h-4" data-id="element-2723" />}
                        {Math.abs(row.trend)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2724">
                      <span className="text-gray-900 font-medium" data-id="element-2725">
                        {row.dbt}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2726">
                      <div className="w-48" data-id="element-2727">
                        {row.limit === 'Pending Decision' ? <span className="text-yellow-600 text-sm font-semibold" data-id="element-2728">
                            Pending Decision
                          </span> : row.limit > 0 ? <>
                            <div className="flex justify-between text-xs mb-1.5" data-id="element-2729">
                              <span className="font-semibold text-gray-900" data-id="element-2730">
                                {formatCurrency(row.balance)}
                              </span>
                              <span className="text-gray-500 font-medium" data-id="element-2731">
                                {utilPct}%
                              </span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden mb-1" data-id="element-2732">
                              <div className="h-full bg-indigo-500 rounded-full" style={{
                          width: `${utilPct}%`
                        }} data-id="element-2733" />
                            </div>
                            <div className="text-right text-[10px] text-gray-400 font-medium" data-id="element-2734">
                              {formatCurrency(row.limit)} limit
                            </div>
                          </> : <span className="text-gray-500 text-sm" data-id="element-2735">
                            No credit extended
                          </span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2736">
                      <span className="text-gray-600" data-id="element-2737">{row.assignedTo}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2738">
                      <div className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-xs font-bold" data-id="element-2739">
                        {row.group}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2740">
                      <div className="flex justify-center" data-id="element-2741">
                        <motion.button whileHover={{
                      scale: 1.05
                    }} whileTap={{
                      scale: 0.95
                    }} className={`px-6 py-2 rounded-lg text-white text-sm font-semibold transition-colors ${isClickable ? 'bg-indigo-600 hover:bg-indigo-700 cursor-pointer' : 'bg-indigo-400 cursor-not-allowed'}`} data-id="element-2742">
                          View
                        </motion.button>
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}